/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

/**
 * QubePro mobil WebView navigasyon politikası.
 *
 * - Internal QubePro URL'leri mobil kabukta aynı WebView'de kalır.
 * - Harici URL'ler native kabuğun `onOpenWindow` politikasına bırakılır.
 * - Host güveni hardcoded ürün adından değil mevcut origin ve Vite'a zaten
 *   aktarılan merkezi platform URL'lerinden türetilir.
 * - `javascript:`, credential içeren URL ve canlı HTTPS sayfadan HTTP internal
 *   navigasyon kabul edilmez.
 */

export type QubeProNavigationDisposition = 'auto' | 'internal' | 'external';

export interface OpenQubeProNavigationOptions {
  disposition?: QubeProNavigationDisposition;
  browserTarget?: string;
  features?: string;
}

interface QubeProMobileBridge {
  postMessage: (message: string) => void;
}

interface MobileNavigationPolicyState {
  refCount: number;
  originalOpen: typeof window.open;
  patchedOpen: typeof window.open;
  clickHandler: (event: MouseEvent) => void;
}

type QubeProMobileWindow = Window & {
  ReactNativeWebView?: QubeProMobileBridge;
  __QUBEPRO_MOBILE_WEBVIEW__?: boolean;
  __QUBEPRO_MOBILE_NAVIGATION_POLICY__?: MobileNavigationPolicyState;
};

const MOBILE_UA_TOKEN = 'QubeProMobile/';
const TRUSTED_ROOT_ENV_KEYS = [
  'VITE_BASE_URL',
  'VITE_PLATFORM_URL',
  'VITE_CORE_WEB_PUBLIC_BASE_URL',
  'VITE_PLATFORM_APPS_URL',
  'VITE_NOTIFICATION_WEB_BASE_URL',
  'VITE_DOCS_BASE_URL',
  'VITE_AI_APP_BASE_URL',
] as const;

function isLocalHostname(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase();
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '::1' ||
    normalized.endsWith('.localhost')
  );
}

function parseHttpUrl(rawUrl: string | URL, baseHref?: string): URL | null {
  try {
    const fallbackBase =
      baseHref ||
      (typeof window !== 'undefined' ? window.location.href : 'https://invalid.local/');
    const parsed = rawUrl instanceof URL ? new URL(rawUrl.toString()) : new URL(String(rawUrl), fallbackBase);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    if (parsed.username || parsed.password) return null;
    return parsed;
  } catch {
    return null;
  }
}

function isSafeInternalScheme(url: URL): boolean {
  if (url.protocol === 'https:') return true;
  if (url.protocol !== 'http:') return false;
  if (isLocalHostname(url.hostname)) return true;
  return typeof window !== 'undefined' && window.location.protocol === 'http:';
}

function configuredTrustedRoots(): URL[] {
  const roots: URL[] = [];
  const seen = new Set<string>();
  const add = (value: unknown) => {
    const raw = String(value ?? '').trim();
    if (!raw) return;
    const parsed = parseHttpUrl(raw);
    if (!parsed) return;
    const key = parsed.origin.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    roots.push(parsed);
  };

  if (typeof window !== 'undefined') {
    add(window.location.href);
    add(document.baseURI);
  }

  const env = import.meta.env as unknown as Record<string, unknown>;
  for (const key of TRUSTED_ROOT_ENV_KEYS) add(env[key]);
  return roots;
}

function hostnameMatchesRoot(candidateHostname: string, rootHostname: string): boolean {
  const candidate = candidateHostname.toLowerCase();
  const root = rootHostname.toLowerCase();
  if (candidate === root) return true;
  if (isLocalHostname(root) || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(root)) return false;
  // `evilqubepro.example` benzeri suffix kaçaklarını engellemek için nokta sınırı zorunlu.
  return root.includes('.') && candidate.endsWith(`.${root}`);
}

export function isQubeProMobileWebView(userAgent?: string): boolean {
  if (typeof window === 'undefined') return false;
  const mobileWindow = window as QubeProMobileWindow;
  if (mobileWindow.__QUBEPRO_MOBILE_WEBVIEW__ === true || mobileWindow.ReactNativeWebView) {
    return true;
  }
  const ua = String(userAgent ?? window.navigator?.userAgent ?? '');
  return ua.includes(MOBILE_UA_TOKEN) || /(?:^|[\s;(])wv(?:[\s;)]|$)/i.test(ua);
}

export function isQubeProInternalNavigation(rawUrl: string | URL): boolean {
  const parsed = parseHttpUrl(rawUrl);
  if (!parsed || !isSafeInternalScheme(parsed)) return false;
  return configuredTrustedRoots().some((root) => hostnameMatchesRoot(parsed.hostname, root.hostname));
}

function navigateInsideCurrentWebView(url: URL): void {
  window.location.assign(url.toString());
}

/**
 * Bilinen internal linklerde `disposition: 'internal'` kullanılabilir. `auto`, mevcut
 * origin/platform köklerinden güven kararı verir; `external` daima native/dış akışa kalır.
 */
export function openQubeProNavigation(
  rawUrl: string | URL,
  options: OpenQubeProNavigationOptions = {},
): Window | null {
  if (typeof window === 'undefined') return null;
  const parsed = parseHttpUrl(rawUrl);
  if (!parsed) return null;

  const disposition = options.disposition ?? 'auto';
  const shouldStayInside =
    disposition !== 'external' &&
    isSafeInternalScheme(parsed) &&
    (disposition === 'internal' || isQubeProInternalNavigation(parsed));

  if (isQubeProMobileWebView() && shouldStayInside) {
    navigateInsideCurrentWebView(parsed);
    return null;
  }

  return window.open(
    parsed.toString(),
    options.browserTarget ?? '_blank',
    options.features ?? 'noopener,noreferrer',
  );
}

/**
 * AuthProvider tarafından bir kez kurulur. Böylece ürün sayfalarındaki mevcut
 * `window.open(..., '_blank')` ve `<a target="_blank">` kullanımları da ortak
 * politikaya uyar; harici linkler ve `download` linkleri değiştirilmez.
 */
export function installQubeProMobileWebViewNavigationPolicy(): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  const mobileWindow = window as QubeProMobileWindow;
  const existing = mobileWindow.__QUBEPRO_MOBILE_NAVIGATION_POLICY__;
  if (existing) {
    existing.refCount += 1;
    return () => {
      existing.refCount = Math.max(0, existing.refCount - 1);
    };
  }

  const originalOpen = window.open.bind(window) as typeof window.open;
  const patchedOpen: typeof window.open = (url, target, features) => {
    if (
      url != null &&
      String(target ?? '').toLowerCase() === '_blank' &&
      isQubeProMobileWebView() &&
      isQubeProInternalNavigation(url)
    ) {
      const parsed = parseHttpUrl(url);
      if (parsed) navigateInsideCurrentWebView(parsed);
      return null;
    }
    return originalOpen(url, target, features);
  };

  const clickHandler = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (!isQubeProMobileWebView() || !(event.target instanceof Element)) return;
    const anchor = event.target.closest<HTMLAnchorElement>('a[href][target="_blank"]');
    if (!anchor || anchor.hasAttribute('download')) return;

    const declaredDisposition = anchor.dataset.qubeproNavigation as QubeProNavigationDisposition | undefined;
    if (declaredDisposition === 'external') return;
    const parsed = parseHttpUrl(anchor.href);
    const shouldStayInside =
      Boolean(parsed) &&
      (declaredDisposition === 'internal' || isQubeProInternalNavigation(parsed!));
    if (!parsed || !shouldStayInside || !isSafeInternalScheme(parsed)) return;

    event.preventDefault();
    navigateInsideCurrentWebView(parsed);
  };

  window.open = patchedOpen;
  document.addEventListener('click', clickHandler, true);
  const state: MobileNavigationPolicyState = { refCount: 1, originalOpen, patchedOpen, clickHandler };
  mobileWindow.__QUBEPRO_MOBILE_NAVIGATION_POLICY__ = state;

  return () => {
    state.refCount = Math.max(0, state.refCount - 1);
    if (state.refCount > 0) return;
    document.removeEventListener('click', state.clickHandler, true);
    if (window.open === state.patchedOpen) window.open = state.originalOpen;
    delete mobileWindow.__QUBEPRO_MOBILE_NAVIGATION_POLICY__;
  };
}
